import { Avatar, Box, Text } from '@factorialco/deprecated-design-system'
import { motion } from 'framer-motion'
import { CommentsCommentFragment } from 'generated/resources/graphql'
import moment, { Moment } from 'moment'

import { Reactions } from 'modules/core/components/Reactions'
import { ReactionsReactionValues } from 'modules/core/components/Reactions/Reaction'
import buildUrl from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'

interface Props {
  comment: CommentsCommentFragment
  onAddReaction?: (
    variables: ReactionsReactionValues,
    parentResourceId: string
  ) => Promise<{ id: string } | undefined>
  onDeleteReaction?: (
    id: string,
    parentResourceId: string,
    resourceId: string,
    textCode: string
  ) => Promise<{ success: boolean } | undefined>
}

const Comment = ({ comment, onAddReaction, onDeleteReaction }: Props) => {
  const formatDate = (date: Moment) =>
    date.calendar({
      sameDay: `[${i18n.t('comments.calendar.today')}], LT`,
      lastDay: `[${i18n.t('comments.calendar.yesterday')}], LT`,
      sameYear: 'dddd LT',
      sameElse: 'LLL',
    })

  return (
    <motion.div
      key={comment.id}
      layout
      animate={{ opacity: 1, scale: 1 }}
      initial={{ opacity: 0, scale: 0.7 }}
      exit={{
        opacity: 0,
        scale: 0.7,
      }}
      transition={{
        opacity: { duration: 0.2 },
        layout: {
          type: 'easeOut',
          duration: 0.2,
        },
      }}
    >
      <Box gap="s16" width="full" alignSelf="flexStart" alignItems="flexStart">
        <Box gap="s8" flexDirection="row">
          <Avatar
            url={buildUrl(comment.author?.avatar)}
            label={comment.author?.fullName || ''}
            size="s40"
          />
          <Box flexDirection="column" gap="s8">
            <Box flexDirection="column" paddingTop="s4">
              <Text.Normal color="grey1000" size="200" weight="semibold">
                {comment.author?.fullName || i18n.t('common.unknown_employee')}
              </Text.Normal>
              <Text.Normal color="grey700" size="200">
                {formatDate(moment(comment.createdAt))}
              </Text.Normal>
            </Box>
            <Text.Normal color="grey1000" size="300">
              {/* This is sanitized in the Comments create interactor */}
              <span dangerouslySetInnerHTML={{ __html: comment.content }} />
            </Text.Normal>

            {onAddReaction && onDeleteReaction && (
              <Reactions
                items={comment.groupedReactions}
                resourceId={comment.id}
                addReaction={(variables) => onAddReaction(variables, comment.resourceId)}
                deleteReaction={(id, resourceId, textCode) =>
                  onDeleteReaction(id, resourceId, comment.resourceId, textCode)
                }
              />
            )}
          </Box>
        </Box>
      </Box>
    </motion.div>
  )
}

export default Comment
